import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, MessageSquare, Zap, LifeBuoy, X } from "lucide-react";

/**
 * MobileHelpFab — bottom-right floating action button (mobile only).
 * Tap the circular yellow "Help" pill → 3 actions bloom upward:
 *   • Call dispatch (tel:+18317477141)
 *   • Text dispatch (sms:+17653301951)
 *   • Request service (routes to /register or /customer based on login state)
 *
 * Hidden on sm+ screens so desktop keeps its normal header actions.
 */
export default function MobileHelpFab({ requestPath = "/register", requestLabel = "Request", enabled = true }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  // Auto-close when the route changes (e.g. after tapping Request)
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (!enabled) return null;

  return (
    <div className="sm:hidden fixed right-3 bottom-3 z-50 flex flex-col items-end gap-2" data-testid="mobile-help-fab">
      {open && (
        <div className="flex flex-col items-end gap-2 animate-in fade-in slide-in-from-bottom-2 duration-150">
          <a
            href="tel:+18317477141"
            data-testid="fab-call"
            className="flex items-center gap-2 pl-3 pr-4 py-3 bg-asap-red text-white font-bold uppercase text-xs tracking-widest border-2 border-asap-deep shadow-hard-sm"
          >
            <Phone size={16}/> Call 831-747-7141
          </a>
          <a
            href="sms:+17653301951"
            data-testid="fab-text"
            className="flex items-center gap-2 pl-3 pr-4 py-3 bg-asap-deep text-white font-bold uppercase text-xs tracking-widest border-2 border-asap-deep shadow-hard-sm"
          >
            <MessageSquare size={16}/> Text 765-330-1951
          </a>
          <button
            onClick={() => { nav(requestPath); setOpen(false); }}
            data-testid="fab-request"
            className="flex items-center gap-2 pl-3 pr-4 py-3 bg-asap-yellow text-asap-deep font-bold uppercase text-xs tracking-widest border-2 border-asap-deep shadow-hard-sm"
          >
            <Zap size={16}/> {requestLabel}
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close help menu" : "Open help menu"}
        aria-expanded={open}
        data-testid="fab-toggle"
        className={`flex items-center gap-2 pl-3 pr-4 py-3 font-bold uppercase text-xs tracking-widest border-2 border-asap-deep shadow-hard transition ${open ? "bg-white text-asap-deep" : "bg-asap-yellow text-asap-deep"}`}
      >
        {open ? <X size={18}/> : <LifeBuoy size={18}/>}
        {open ? "Close" : "Help"}
      </button>
    </div>
  );
}
